import { Phone, Mail, MapPin, ExternalLink, Instagram, Linkedin, Send } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { apiService } from '../services/api'
import toast from 'react-hot-toast'
import mapImg from '../assets/flightcase_map.jpg'

const Contact = () => {
  const phone = '(+590) 590 26 54 30'
  const email = 'planning@flightcasefwi.com'
  const address = "Imm. Oiseau du Paradis, ZI Jarry, 42 Rue Henri Becquerel, Baie-Mahault 97122"
  const mapsUrl = 'https://maps.app.goo.gl/mfwNS5F55UkmyeTJ8'
  
  // État du formulaire
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState(null)
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)
  const recaptchaRef = useRef(null)
  
  // Clé reCAPTCHA site (à mettre dans les variables d'environnement)
  const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''
  
  // Initialiser reCAPTCHA
  useEffect(() => {
    // Créer un callback global pour reCAPTCHA
    if (RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '') {
      window.handleRecaptchaCallback = (token) => {
        setRecaptchaToken(token)
      }
      
      window.handleRecaptchaExpired = () => {
        setRecaptchaToken(null)
      }
      
      window.handleRecaptchaError = () => {
        setRecaptchaToken(null)
        toast.error('Erreur lors de la vérification reCAPTCHA. Veuillez réessayer.')
      }
      
      // Fonction pour rendre le widget reCAPTCHA
      const renderRecaptchaWidget = () => {
        if (window.grecaptcha && recaptchaRef.current) {
          window.grecaptcha.ready(() => {
            try {
              if (!recaptchaRef.current.hasChildNodes()) {
                window.grecaptcha.render(recaptchaRef.current, {
                  sitekey: RECAPTCHA_SITE_KEY,
                  callback: window.handleRecaptchaCallback,
                  'expired-callback': window.handleRecaptchaExpired,
                  'error-callback': window.handleRecaptchaError
                })
              }
              setRecaptchaLoaded(true)
            } catch (error) {
              // Le widget se rendra automatiquement via les attributs data-*
              setRecaptchaLoaded(true)
            }
          })
        }
      }
      
      // Charger le script reCAPTCHA si ce n'est pas déjà fait
      if (!window.grecaptcha && !document.querySelector('script[src*="recaptcha"]')) {
        const script = document.createElement('script')
        script.src = 'https://www.google.com/recaptcha/api.js'
        script.async = true
        script.defer = true
        script.onload = () => {
          setRecaptchaLoaded(true)
          renderRecaptchaWidget()
        }
        script.onerror = () => {
          setRecaptchaLoaded(false)
        }
        document.head.appendChild(script)
      } else if (window.grecaptcha) {
        // Si grecaptcha est déjà chargé, rendre le widget
        setRecaptchaLoaded(true)
        renderRecaptchaWidget()
      } else {
        // Attendre que le script se charge
        const checkInterval = setInterval(() => {
          if (window.grecaptcha) {
            clearInterval(checkInterval)
            setRecaptchaLoaded(true)
            renderRecaptchaWidget()
          }
        }, 100)
        
        // Timeout après 10 secondes
        setTimeout(() => {
          clearInterval(checkInterval)
          if (!window.grecaptcha) {
            setRecaptchaLoaded(false)
          }
        }, 10000)
      }
    }
    
    // Nettoyage
    return () => {
      if (window.handleRecaptchaCallback) {
        delete window.handleRecaptchaCallback
      }
      if (window.handleRecaptchaExpired) {
        delete window.handleRecaptchaExpired
      }
      if (window.handleRecaptchaError) {
        delete window.handleRecaptchaError
      }
      // Réinitialiser le widget si nécessaire
      if (window.grecaptcha && recaptchaRef.current) {
        try {
          const widgetId = recaptchaRef.current.getAttribute('data-widget-id')
          if (widgetId) {
            window.grecaptcha.reset(widgetId)
          }
        } catch (e) {
          // Ignorer les erreurs de nettoyage
        }
      }
    }
  }, [RECAPTCHA_SITE_KEY])
  
  // Gestion des changements de formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }
  
  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation côté client
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }
    
    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      toast.error('Veuillez saisir une adresse email valide')
      return
    }
    
    // Vérification reCAPTCHA
    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      toast.error('Veuillez compléter la vérification reCAPTCHA')
      return
    }
    
    setIsSubmitting(true)
    
    try {
      const response = await apiService.post('/emails/contact', {
        ...formData,
        recaptchaToken: recaptchaToken
      })
      
      if (response.data.success) {
        toast.success(response.data.message)
        // Réinitialiser le formulaire
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        })
        // Réinitialiser reCAPTCHA
        if (window.grecaptcha && recaptchaRef.current) {
          window.grecaptcha.reset()
          setRecaptchaToken(null)
        }
      } else {
        toast.error(response.data.error || 'Erreur lors de l\'envoi du message')
        // Réinitialiser reCAPTCHA en cas d'erreur
        if (window.grecaptcha && recaptchaRef.current) {
          window.grecaptcha.reset()
          setRecaptchaToken(null)
        }
      }
    } catch (error) {
      console.error('Erreur envoi formulaire contact:', error)
      const errorMessage = error.response?.data?.error || 'Erreur lors de l\'envoi du message. Veuillez réessayer.'
      toast.error(errorMessage)
      // Réinitialiser reCAPTCHA en cas d'erreur
      if (window.grecaptcha && recaptchaRef.current) {
        window.grecaptcha.reset()
        setRecaptchaToken(null)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">Contact</h1>
          <p className="text-xl text-gray-600">Nous sommes à votre écoute</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Coordonnées</h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-orange-500" />
                <a href={`tel:${phone.replace(/[^+0-9]/g,'')}`} className="text-gray-800 hover:text-orange-600">
                  {phone}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-orange-500" />
                <a href={`mailto:${email}`} className="text-gray-800 hover:text-orange-600">
                  {email}
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-orange-500 mt-0.5" />
                <div className="text-gray-800">
                  {address}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Horaires</h2>
            <p className="text-gray-600">Du lundi au vendredi de 9h00 à 12h00 et de 14h00 à 18h00</p>
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Localisation</h3>
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center text-orange-600 hover:underline">
                Voir sur Google Maps <ExternalLink className="h-4 w-4 ml-1" />
              </a>
            </div>
          </div>
        </div>

        {/* Réseaux sociaux */}
        <div className="card mt-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Suivez-nous</h2>
          <p className="text-gray-600 mb-6">Restez connecté avec FlightCase FWI sur nos réseaux sociaux</p>
          <div className="flex flex-wrap gap-4">
            <a 
              href="https://www.facebook.com/FlightcaseFWI" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-3 px-4 py-3 bg-orange-100 hover:bg-orange-600 text-orange-600 hover:text-white rounded-lg transition-colors"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span>Facebook</span>
            </a>
            <a 
              href="https://www.instagram.com/flightcase.fwi" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-3 px-4 py-3 bg-orange-100 hover:bg-orange-600 text-orange-600 hover:text-white rounded-lg transition-colors"
            >
              <Instagram className="h-5 w-5" />
              <span>Instagram</span>
            </a>
            <a 
              href="https://www.linkedin.com/company/flightcase-fwi/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-3 px-4 py-3 bg-orange-100 hover:bg-orange-600 text-orange-600 hover:text-white rounded-lg transition-colors"
            >
              <Linkedin className="h-5 w-5" />
              <span>LinkedIn</span>
            </a>
            <a 
              href="https://wa.me/590590265430" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-3 px-4 py-3 bg-orange-100 hover:bg-orange-600 text-orange-600 hover:text-white rounded-lg transition-colors"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
              <span>WhatsApp</span>
            </a>
          </div>
        </div>

        {/* Formulaire de contact */}
        <div className="card mt-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Formulaire de contact</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom <span className="text-red-500">*</span>
              </label>
              <input 
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="input-field" 
                placeholder="Votre nom complet"
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="input-field" 
                placeholder="votre@email.com"
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
              <input 
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="input-field" 
                placeholder="06 12 34 56 78"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sujet</label>
              <input 
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                className="input-field" 
                placeholder="Objet de votre demande"
                disabled={isSubmitting}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea 
                rows={5} 
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="input-field" 
                placeholder="Décrivez votre demande en détail..."
                required
                disabled={isSubmitting}
              ></textarea>
            </div>
            {RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '' && (
              <div className="md:col-span-2">
                <div 
                  ref={recaptchaRef}
                  className="g-recaptcha"
                  data-sitekey={RECAPTCHA_SITE_KEY}
                  data-callback="handleRecaptchaCallback"
                  data-expired-callback="handleRecaptchaExpired"
                  data-error-callback="handleRecaptchaError"
                ></div>
                {!recaptchaToken && (
                  <p className="text-sm text-gray-500 mt-2">
                    Veuillez compléter la vérification reCAPTCHA ci-dessus pour activer le bouton d'envoi
                  </p>
                )}
                {!recaptchaLoaded && RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '' && (
                  <p className="text-sm text-orange-600 mt-2">
                    ⚠️ Le script reCAPTCHA est en cours de chargement... Si le problème persiste, rechargez la page.
                  </p>
                )}
              </div>
            )}
            <div className="md:col-span-2 text-right">
              <button 
                type="submit" 
                disabled={isSubmitting || (RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '' && !recaptchaToken)}
                className={`btn-primary flex items-center space-x-2 ${isSubmitting || (RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '' && !recaptchaToken) ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '' && !recaptchaToken ? 'Veuillez compléter la vérification reCAPTCHA' : ''}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Envoyer le message</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

      </div>
      {/* Carte plein largeur, collée au footer */}
      <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]">
        <img src={mapImg} alt="Localisation Flightcase" className="w-full h-[420px] md:h-[520px] object-cover" />
      </div>
    </div>
  )
}

export default Contact


